/**
 * Build a MovementPlan for one committed placement.
 *
 * Procedures (see resolveProcedure):
 *  - self_park    : final → taxi via gate → park → taxi-out toward gate → climb-out
 *  - drop_then_tow: final → taxi to a drop-off node → pax/attach dwell → TUG tows in;
 *                   on departure the tug tows out to a pushback node, detaches, the
 *                   aircraft self-taxis to the runway and climbs out
 *  - direct_tow   : like drop_then_tow but the tug meets the aircraft at the gate
 *                   (no separate drop-off dwell)
 *
 * Inbound legs are scheduled BACKWARD from `arrival`, outbound FORWARD from
 * `departure`; the parked dwell is exactly [arrival, departure]. The tug rides
 * `NOSE_TUG_FT` ahead of the aircraft nose (along travel when pulling in, along
 * the parked heading when pushing back), so the coupling needs no runtime state —
 * both are pure functions of time (scrub-safe).
 */
import type { AircraftCatalog, CatalogEntry, EventPlacement } from '../engine/types'
import type { MotionConfig, Procedure } from '../data/motionConfig'
import { nearestGate, type FieldGraph } from '../data/fieldGraph'
import { headingToForward, smoothPath, type Pt } from './easing'
import { sceneRotationToCompass } from '../engine/heading'
import { routeTaxi } from './routeTaxi'
import { routeGround, type RouteGroundOpts } from './routeGround'
import { runwayPerf, rollGeometryFor, type RunwayPerf } from './runwayPerformance'
import type { TaxiPlan } from '../data/taxiGraph'
import type { MotionDwell, MotionLeg, MovementPlan } from './movementPlan'
import type { FuelRequest } from './fuelRequest'
import { SLOT_MS } from '../data/eventWindow'

/**
 * Route a ground leg via the authored taxi network when a plan is present (so
 * edits/closures reroute), else fall back to the single-spine heuristic.
 * Evaluated at the leg's own time so scheduled closures apply per-aircraft.
 */
function groundPath(
  plan: TaxiPlan | null | undefined,
  graph: FieldGraph,
  from: Pt,
  to: Pt,
  atMs: number,
  opts?: RouteGroundOpts
): Pt[] {
  return plan ? routeGround(plan, from, to, atMs, graph, opts) : routeTaxi(graph, from, to)
}

/** Roles a landing rollout snaps onto when leaving the runway, best-first. */
const RUNWAY_EXIT_ROLES = ['runway_exit', 'exit_to_taxiway'] as const
/** Roles a departure joins the runway through, best-first. */
const RUNWAY_ENTRY_ROLES = ['hold_short', 'runway_exit', 'exit_to_taxiway'] as const
/** Max feet a smoothed taxi route may bow off the routed polyline (keeps turns
 *  on pavement instead of cutting the corner when leaving/joining the runway). */
const TAXI_SMOOTH_DEV_FT = 25

/** Scene object ids (match eventItemToPlaced + the tug / fuel-truck renderers). */
export const aircraftActorId = (eventAllocationId: string) => `evt:${eventAllocationId}`
export const tugActorId = (eventAllocationId: string) => `tug:${eventAllocationId}`
export const fuelTruckActorId = (eventAllocationId: string) => `fuel:${eventAllocationId}`

/** Prefixes owned by the event planner — everything else in the scene is not ours. */
const ACTOR_ID_PREFIXES = ['evt:', 'tug:', 'fuel:'] as const

/**
 * Is this scene-object id one of the planner's actors?
 *
 * The scope guard for every bulk purge: it lets the planner clear its own stage
 * (including objects it lost track of) while leaving the location's real
 * allocations — named by UUID — untouched.
 */
export const isEventActorId = (id: string): boolean =>
  ACTOR_ID_PREFIXES.some((p) => id.startsWith(p))

/**
 * How far ahead of the aircraft's reference point the tug rides, along the
 * aircraft's nose. Approximates (centre→nose) + towbar so the tug sits in
 * front of the nose gear rather than beside the fuselage. (Stage F can refine
 * this per aircraft from the catalogued length / nose-gear offset.)
 */
const NOSE_TUG_FT = 42
/** Towbar reach from the nose gear to the tug body, feet. */
const TOWBAR_FT = 10

/**
 * Per-aircraft tug standoff: how far ahead of the aircraft reference point (its
 * footprint centre) the tug sits. Uses the catalogued `nose_gear_from_nose` to
 * place the hitch at the nose gear when known, else ~42% of the length; plus a
 * towbar. Falls back to the `NOSE_TUG_FT` constant when the type is unknown.
 */
function tugNoseOffset(entry?: CatalogEntry): number {
  if (!entry) return NOSE_TUG_FT
  const length = entry.footprint.length
  const centreToNoseGear =
    entry.noseGearFromNose != null
      ? Math.max(6, length / 2 - entry.noseGearFromNose)
      : length * 0.42
  return centreToNoseGear + TOWBAR_FT
}

const ptDist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1])

/** Forward unit vector for a heading in degrees (scene feet [x, z]). */
const fwdOf = (headingDeg: number): Pt => headingToForward(headingDeg)

/**
 * Whether the field graph carries real on-runway roll geometry (authored runway)
 * vs. the synth fallback where roll points collapse onto the thresholds.
 */
function rollFlags(graph: FieldGraph, cfg: MotionConfig) {
  const landing = cfg.enableRunwayRoll && ptDist(graph.touchdownPoint, graph.rolloutEnd) > 1
  const takeoff = cfg.enableRunwayRoll && ptDist(graph.thresholdDeparture, graph.rotationPoint) > 1
  return { landing, takeoff }
}

const lerpPt = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
function unit(a: Pt, b: Pt): Pt {
  const dx = b[0] - a[0]
  const dz = b[1] - a[1]
  const len = Math.hypot(dx, dz)
  return len < 1e-6 ? [0, 0] : [dx / len, dz / len]
}
const offset = (p: Pt, dir: Pt, d: number): Pt => [p[0] + dir[0] * d, p[1] + dir[1] * d]
const clamp01 = (x: number, hi = 1): number => Math.max(0, Math.min(hi, x))

// ── Tuning fractions (named for readability; identical values to before) ─────
/** Clamp ceilings for the airborne / on-runway-roll portions of the lead/tail. */
const AIR_FRAC_MAX = 0.9
const ROLL_FRAC_MAX = 0.8
/** Attach/detach dwell can't exceed this fraction of the inbound lead / outbound tail. */
const INBOUND_DWELL_CAP_FRAC = 0.4
const OUTBOUND_DWELL_CAP_FRAC = 0.4
/** Ground-move split between the self-taxi segment and the towed segment. */
const TOW_TAXI_FRAC = 0.6
const TOW_TOW_FRAC = 0.4
/** Inbound handoff / outbound pushback node placement along gate↔spot. */
const HANDOFF_LERP = 0.3
const PUSHBACK_LERP = 0.4
/** Empty-tug drive budget: `max(SLOT_MS * slot, exitTailMs * tail)`. */
const TUG_DRIVE_SLOT_FRAC = 0.5
const TUG_DRIVE_TAIL_FRAC = 0.25
/** Hold-short line-up dwell: `min(SLOT_MS * slot, taxiOutMs * taxi)`. */
const HOLDSHORT_SLOT_FRAC = 0.2
const HOLDSHORT_TAXI_FRAC = 0.25

/**
 * Shared lead-in / tail-out time budget. Both self-park and tow split a window
 * (approach lead inbound, exit tail outbound) into an optional dwell, then an
 * airborne portion (further split into descent/climb vs. on-runway roll) and a
 * ground portion (taxi, plus the towed segment for tows).
 *
 * `groundExpr` preserves byte-exact parity with the two legacy call shapes:
 *  - `'complement'` reproduces self-park's `windowMs * (1 - airFrac)` (no dwell),
 *  - `'subtract'`   reproduces tow's `movingMs - airMs`.
 * They are NOT IEEE-754 bit-identical, so the flag is load-bearing, not cosmetic.
 */
interface PhaseBudget {
  movingMs: number
  airMs: number
  rollMs: number
  /** descent (inbound) / climb (outbound) time = airMs − rollMs. */
  flightMs: number
  groundMs: number
}
function phaseBudget(
  windowMs: number,
  dwellMs: number,
  airFrac: number,
  rollFrac: number,
  hasRoll: boolean,
  groundExpr: 'complement' | 'subtract'
): PhaseBudget {
  const movingMs = windowMs - dwellMs
  const airMs = movingMs * airFrac
  const rollMs = hasRoll ? airMs * rollFrac : 0
  const flightMs = airMs - rollMs
  const groundMs = groundExpr === 'complement' ? windowMs * (1 - airFrac) : movingMs - airMs
  return { movingMs, airMs, rollMs, flightMs, groundMs }
}

// ── Shared airborne legs ─────────────────────────────────────────────────────
// Self-park and tow build identical airborne legs (only the surrounding taxi/tow
// legs and the inbound push ORDER differ), so the leg objects are constructed
// here once. Each returns null when not flown; the caller pushes it in its own
// order via `pushLeg`, preserving each procedure's existing leg sequence.

type RollGeom = ReturnType<typeof rollGeometryFor>

const pushLeg = (legs: MotionLeg[], leg: MotionLeg | null): void => {
  if (leg) legs.push(leg)
}

/** Descending final approach onto `landAt` (touchdown point or threshold). */
function finalLeg(acId: string, cfg: MotionConfig, graph: FieldGraph, landAt: Pt, startT: number, endT: number, airborne: boolean, descentMs: number): MotionLeg | null {
  if (!(airborne && descentMs > 0)) return null
  return { actorId: acId, kind: 'final', startT, endT, path: [graph.finalEntry, landAt], ease: 'inOut', headingMode: 'fixed', fixedHeadingDeg: graph.approachBearingDeg, altStartFt: cfg.finalAltFt, altEndFt: 0 }
}

/** On-runway landing rollout (touchdown → rollout end), decelerating. */
function landingRollLeg(acId: string, graph: FieldGraph, rg: RollGeom, startT: number, endT: number, airborne: boolean, rollMs: number): MotionLeg | null {
  if (!(airborne && rollMs > 0)) return null
  return { actorId: acId, kind: 'landing_roll', startT, endT, path: [rg.touchdownPoint, rg.rolloutEnd], ease: 'out', headingMode: 'fixed', fixedHeadingDeg: graph.approachBearingDeg }
}

/** On-runway takeoff roll (departure threshold → rotation point), accelerating. */
function takeoffRollLeg(acId: string, graph: FieldGraph, rg: RollGeom, startT: number, endT: number, airborne: boolean, rollMs: number): MotionLeg | null {
  if (!(airborne && rollMs > 0)) return null
  return { actorId: acId, kind: 'takeoff_roll', startT, endT, path: [graph.thresholdDeparture, rg.rotationPoint], ease: 'in', headingMode: 'fixed', fixedHeadingDeg: graph.departureBearingDeg }
}

/** Climb-out from `climbFrom` (rotation point or threshold) to the climb exit. */
function climbLeg(acId: string, cfg: MotionConfig, graph: FieldGraph, climbFrom: Pt, startT: number, endT: number, airborne: boolean, climbMs: number): MotionLeg | null {
  if (!(airborne && climbMs > 0)) return null
  return { actorId: acId, kind: 'climb', startT, endT, path: [climbFrom, graph.climbExit], ease: 'in', headingMode: 'fixed', fixedHeadingDeg: graph.departureBearingDeg, altStartFt: 0, altEndFt: cfg.finalAltFt }
}

// ── Self-park (no tug) ──────────────────────────────────────────────────────

function straightSelfPark(p: EventPlacement, cfg: MotionConfig): MovementPlan {
  const acId = aircraftActorId(p.eventAllocationId)
  const spot: Pt = [p.position[0], p.position[1]]
  // Scene Y-rotation → compass (motion convention); converted back on output.
  const heading = sceneRotationToCompass(p.rotation)
  const [fx, fz] = headingToForward(heading)
  const entry: Pt = [spot[0] - fx * cfg.taxiRunFt, spot[1] - fz * cfg.taxiRunFt]
  const exit: Pt = [spot[0] + fx * cfg.taxiRunFt, spot[1] + fz * cfg.taxiRunFt]
  const approachStart = p.arrival - cfg.approachLeadMs
  const exitEnd = p.departure + cfg.exitTailMs
  return {
    eventAllocationId: p.eventAllocationId,
    startT: approachStart,
    endT: exitEnd,
    actors: [{ kind: 'aircraft', id: acId }],
    legs: [
      { actorId: acId, kind: 'taxi', startT: approachStart, endT: p.arrival, path: [entry, spot], ease: 'out', headingMode: 'fixed', fixedHeadingDeg: heading },
      { actorId: acId, kind: 'taxi', startT: p.departure, endT: exitEnd, path: [spot, exit], ease: 'in', headingMode: 'fixed', fixedHeadingDeg: heading },
    ],
    dwells: [{ actorId: acId, startT: p.arrival, endT: p.departure, position: spot, headingDeg: heading }],
  }
}

function buildSelfParkPlan(
  p: EventPlacement,
  cfg: MotionConfig,
  graph: FieldGraph,
  perf: RunwayPerf,
  plan?: TaxiPlan | null
): MovementPlan {
  const acId = aircraftActorId(p.eventAllocationId)
  const spot: Pt = [p.position[0], p.position[1]]
  // `p.rotation` is a scene Y-rotation; the motion math below works in the compass
  // convention (see easing.ts), so convert at the input boundary. computeActorPose
  // converts back to scene rotation on output.
  const parkedHeading = sceneRotationToCompass(p.rotation)
  const airborne = cfg.enableAirborneFinal
  const airFrac = airborne ? clamp01(cfg.airborneFraction, AIR_FRAC_MAX) : 0
  const roll = rollFlags(graph, cfg)
  const rollFrac = clamp01(cfg.rollFraction, ROLL_FRAC_MAX)
  // Per-aircraft on-runway geometry: bigger aircraft roll out / accelerate longer.
  const rg = rollGeometryFor(graph, perf, cfg)
  const legs: MotionLeg[] = []

  // ── Inbound (backward from arrival): final descent → [landing rollout] → taxi.
  const inB = phaseBudget(cfg.approachLeadMs, 0, airFrac, rollFrac, airborne && roll.landing, 'complement')
  const taxiInMs = inB.groundMs
  const airInMs = inB.airMs
  const rollInMs = inB.rollMs
  const descentMs = inB.flightMs
  // Land onto the touchdown point (then roll) when a real runway is authored;
  // otherwise descend straight to the threshold (legacy) and taxi from there.
  const landAt = roll.landing ? rg.touchdownPoint : graph.thresholdApproach
  const taxiFromIn = roll.landing ? rg.rolloutEnd : graph.thresholdApproach

  const taxiInStart = p.arrival - taxiInMs
  const rollInEnd = taxiInStart
  const rollInStart = rollInEnd - rollInMs
  const descentEnd = rollInStart
  const descentStart = descentEnd - descentMs

  legs.push({ actorId: acId, kind: 'taxi', startT: taxiInStart, endT: p.arrival, path: smoothPath(groundPath(plan, graph, taxiFromIn, spot, p.arrival, roll.landing ? { entryRoles: RUNWAY_EXIT_ROLES } : undefined), 8, TAXI_SMOOTH_DEV_FT), ease: 'out', headingMode: 'tangent', ...(roll.landing ? { launchHeadingDeg: graph.approachBearingDeg } : {}), settleHeadingDeg: parkedHeading })
  pushLeg(legs, landingRollLeg(acId, graph, rg, rollInStart, rollInEnd, airborne, rollInMs))
  pushLeg(legs, finalLeg(acId, cfg, graph, landAt, descentStart, descentEnd, airborne, descentMs))
  const startT = airborne && airInMs > 0 ? descentStart : taxiInStart

  // ── Outbound (forward from departure): taxi → [hold short] → [takeoff roll] → climb.
  const outB = phaseBudget(cfg.exitTailMs, 0, airFrac, rollFrac, airborne && roll.takeoff, 'complement')
  const taxiOutMs = outB.groundMs
  const airOutMs = outB.airMs
  const rollOutMs = outB.rollMs
  const climbMs = outB.flightMs
  const climbFrom = roll.takeoff ? rg.rotationPoint : graph.thresholdDeparture
  // Line up and hold briefly at the runway before the takeoff roll (carved from
  // the taxi budget so the total outbound timing is unchanged).
  const holdShortMs = airborne && roll.takeoff ? Math.min(SLOT_MS * HOLDSHORT_SLOT_FRAC, taxiOutMs * HOLDSHORT_TAXI_FRAC) : 0

  const taxiOutEnd = p.departure + (taxiOutMs - holdShortMs)
  const rollOutStart = taxiOutEnd + holdShortMs
  const rollOutEnd = rollOutStart + rollOutMs
  const climbStart = rollOutEnd
  const climbEnd = climbStart + climbMs

  legs.push({ actorId: acId, kind: 'taxi', startT: p.departure, endT: taxiOutEnd, path: smoothPath(groundPath(plan, graph, spot, graph.thresholdDeparture, p.departure, roll.takeoff ? { goalRoles: RUNWAY_ENTRY_ROLES } : undefined), 8, TAXI_SMOOTH_DEV_FT), ease: 'in', headingMode: 'tangent', launchHeadingDeg: parkedHeading, ...(roll.takeoff ? { settleHeadingDeg: graph.departureBearingDeg } : {}) })
  pushLeg(legs, takeoffRollLeg(acId, graph, rg, rollOutStart, rollOutEnd, airborne, rollOutMs))
  pushLeg(legs, climbLeg(acId, cfg, graph, climbFrom, climbStart, climbEnd, airborne, climbMs))
  const endT = airborne && airOutMs > 0 ? climbEnd : taxiOutEnd

  const dwells: MotionDwell[] = [{ actorId: acId, startT: p.arrival, endT: p.departure, position: spot, headingDeg: parkedHeading }]
  if (holdShortMs > 0) {
    dwells.push({ actorId: acId, startT: taxiOutEnd, endT: rollOutStart, position: graph.thresholdDeparture, headingDeg: graph.departureBearingDeg })
  }

  return {
    eventAllocationId: p.eventAllocationId,
    startT,
    endT,
    actors: [{ kind: 'aircraft', id: acId }],
    legs,
    dwells,
  }
}

// ── Tow (tug-assisted) ──────────────────────────────────────────────────────

function buildTowPlan(
  p: EventPlacement,
  cfg: MotionConfig,
  graph: FieldGraph,
  procedure: Procedure,
  perf: RunwayPerf,
  noseTugFt: number,
  plan?: TaxiPlan | null
): MovementPlan {
  const acId = aircraftActorId(p.eventAllocationId)
  const tugId = tugActorId(p.eventAllocationId)
  const spot: Pt = [p.position[0], p.position[1]]
  // `p.rotation` is a scene Y-rotation; the motion math below works in the compass
  // convention (see easing.ts), so convert at the input boundary. computeActorPose
  // converts back to scene rotation on output.
  const parkedHeading = sceneRotationToCompass(p.rotation)
  const gate = nearestGate(graph, spot)
  const staging = gate.center
  const airborne = cfg.enableAirborneFinal
  const airFrac = airborne ? clamp01(cfg.airborneFraction, AIR_FRAC_MAX) : 0
  const withDropoff = procedure === 'drop_then_tow'

  // Handoff (inbound) + pushback (outbound) nodes between the gate and the spot.
  const handoff: Pt = withDropoff ? lerpPt(gate.center, spot, HANDOFF_LERP) : gate.center
  const pushback: Pt = lerpPt(spot, gate.center, PUSHBACK_LERP)

  const legs: MotionLeg[] = []
  const dwells: MotionDwell[] = []
  const tugDriveMs = Math.max(SLOT_MS * TUG_DRIVE_SLOT_FRAC, cfg.exitTailMs * TUG_DRIVE_TAIL_FRAC)

  // ── INBOUND (backward from arrival) ──
  // Budget: dwell(s) capped to 40% of the lead so motion still reads.
  const roll = rollFlags(graph, cfg)
  const rollFrac = clamp01(cfg.rollFraction, ROLL_FRAC_MAX)
  const rg = rollGeometryFor(graph, perf, cfg)
  const attachInMs = Math.min((withDropoff ? cfg.dropoffDwellMs : 0) + cfg.tugCoupleDwellMs, cfg.approachLeadMs * INBOUND_DWELL_CAP_FRAC)
  const inB = phaseBudget(cfg.approachLeadMs, attachInMs, airFrac, rollFrac, airborne && roll.landing, 'subtract')
  const airInMs = inB.airMs
  const rollInMs = inB.rollMs
  const descentMs = inB.flightMs
  const groundInMs = inB.groundMs
  const taxiInMs = groundInMs * TOW_TAXI_FRAC
  const towInMs = groundInMs * TOW_TOW_FRAC
  const landAt = roll.landing ? rg.touchdownPoint : graph.thresholdApproach
  const taxiFromIn = roll.landing ? rg.rolloutEnd : graph.thresholdApproach

  const towInEnd = p.arrival
  const towInStart = towInEnd - towInMs
  const dwellInEnd = towInStart
  const dwellInStart = dwellInEnd - attachInMs
  const taxiInEnd = dwellInStart
  const taxiInStart = taxiInEnd - taxiInMs
  const rollInEnd = taxiInStart
  const rollInStart = rollInEnd - rollInMs
  const descentEnd = rollInStart
  const descentStart = descentEnd - descentMs

  // Aircraft inbound: final → [landing rollout] → taxi(rollout→gate→handoff) → dwell → towed(handoff→spot).
  pushLeg(legs, finalLeg(acId, cfg, graph, landAt, descentStart, descentEnd, airborne, descentMs))
  pushLeg(legs, landingRollLeg(acId, graph, rg, rollInStart, rollInEnd, airborne, rollInMs))
  legs.push({ actorId: acId, kind: 'taxi', startT: taxiInStart, endT: taxiInEnd, path: smoothPath(groundPath(plan, graph, taxiFromIn, handoff, p.arrival, roll.landing ? { entryRoles: RUNWAY_EXIT_ROLES } : undefined), 8, TAXI_SMOOTH_DEV_FT), ease: 'inOut', headingMode: 'tangent', ...(roll.landing ? { launchHeadingDeg: graph.approachBearingDeg } : {}) })
  const towInDir = unit(handoff, spot)
  dwells.push({ actorId: acId, startT: dwellInStart, endT: dwellInEnd, position: handoff, headingDeg: parkedHeading })
  legs.push({ actorId: acId, kind: 'tow', startT: towInStart, endT: towInEnd, path: [handoff, spot], ease: 'out', headingMode: 'tangent', settleHeadingDeg: parkedHeading })

  // Tug inbound (a forward pull into the spot): the aircraft is towed nose-first,
  // so the tug rides ahead of the nose along the travel direction.
  const hTug = offset(handoff, towInDir, noseTugFt)
  const spotTugIn = offset(spot, towInDir, noseTugFt)
  const tugInEmptyStart = dwellInStart - tugDriveMs
  legs.push({ actorId: tugId, kind: 'tug_empty', startT: tugInEmptyStart, endT: dwellInStart, path: [staging, hTug], ease: 'inOut', headingMode: 'tangent' })
  dwells.push({ actorId: tugId, startT: dwellInStart, endT: dwellInEnd, position: hTug, headingDeg: parkedHeading })
  legs.push({ actorId: tugId, kind: 'tow', startT: towInStart, endT: towInEnd, path: [hTug, spotTugIn], ease: 'out', headingMode: 'tangent', settleHeadingDeg: parkedHeading })
  legs.push({ actorId: tugId, kind: 'tug_empty', startT: towInEnd, endT: towInEnd + tugDriveMs, path: [spotTugIn, staging], ease: 'inOut', headingMode: 'tangent' })

  // ── OUTBOUND (forward from departure) ──
  const detachMs = cfg.tugCoupleDwellMs
  const attachOutMs = cfg.tugCoupleDwellMs
  const dwellOutMs = Math.min(attachOutMs + detachMs, cfg.exitTailMs * OUTBOUND_DWELL_CAP_FRAC)
  const outB = phaseBudget(cfg.exitTailMs, dwellOutMs, airFrac, rollFrac, airborne && roll.takeoff, 'subtract')
  const airOutMs = outB.airMs
  const rollOutMs = outB.rollMs
  const climbMs = outB.flightMs
  const groundOutMs = outB.groundMs
  const towOutMs = groundOutMs * TOW_TOW_FRAC
  const taxiOutMs = groundOutMs * TOW_TAXI_FRAC
  const halfDwell = dwellOutMs / 2
  const climbFrom = roll.takeoff ? rg.rotationPoint : graph.thresholdDeparture
  // Line up and hold briefly at the runway before the takeoff roll.
  const holdShortMs = airborne && roll.takeoff ? Math.min(SLOT_MS * HOLDSHORT_SLOT_FRAC, taxiOutMs * HOLDSHORT_TAXI_FRAC) : 0

  const attachOutStart = p.departure
  const attachOutEnd = attachOutStart + halfDwell
  const towOutStart = attachOutEnd
  const towOutEnd = towOutStart + towOutMs
  const detachOutStart = towOutEnd
  const detachOutEnd = detachOutStart + halfDwell
  const taxiOutStart = detachOutEnd
  const taxiOutEnd = taxiOutStart + (taxiOutMs - holdShortMs)
  const rollOutStart = taxiOutEnd + holdShortMs
  const rollOutEnd = rollOutStart + rollOutMs
  const climbStart = rollOutEnd
  const climbEnd = climbStart + climbMs

  // Aircraft outbound: attach dwell → PUSHBACK(spot→pushback) → detach dwell → taxi(pushback→gate→threshold) → [takeoff roll] → climb.
  // Pushback is a reverse: the nose stays at the parked heading while the aircraft
  // is pushed tail-first to the pushback node (a real ramp pushback), so the tug
  // sits ahead of the nose pushing — not beside the fuselage.
  const parkedFwd = fwdOf(parkedHeading)
  dwells.push({ actorId: acId, startT: attachOutStart, endT: attachOutEnd, position: spot, headingDeg: parkedHeading })
  legs.push({ actorId: acId, kind: 'tow', startT: towOutStart, endT: towOutEnd, path: [spot, pushback], ease: 'inOut', headingMode: 'fixed', fixedHeadingDeg: parkedHeading })
  legs.push({ actorId: acId, kind: 'taxi', startT: taxiOutStart, endT: taxiOutEnd, path: smoothPath(groundPath(plan, graph, pushback, graph.thresholdDeparture, p.departure, roll.takeoff ? { goalRoles: RUNWAY_ENTRY_ROLES } : undefined), 8, TAXI_SMOOTH_DEV_FT), ease: 'in', headingMode: 'tangent', launchHeadingDeg: parkedHeading, ...(roll.takeoff ? { settleHeadingDeg: graph.departureBearingDeg } : {}) })
  pushLeg(legs, takeoffRollLeg(acId, graph, rg, rollOutStart, rollOutEnd, airborne, rollOutMs))
  pushLeg(legs, climbLeg(acId, cfg, graph, climbFrom, climbStart, climbEnd, airborne, climbMs))
  // detach dwell (aircraft static at pushback) — hold the parked heading so the
  // following taxi leg eases the turn-out (via launchHeadingDeg) rather than snapping.
  dwells.push({ actorId: acId, startT: detachOutStart, endT: detachOutEnd, position: pushback, headingDeg: parkedHeading })
  if (holdShortMs > 0) {
    // Line up + hold at the runway before the takeoff roll.
    dwells.push({ actorId: acId, startT: taxiOutEnd, endT: rollOutStart, position: graph.thresholdDeparture, headingDeg: graph.departureBearingDeg })
  }

  // Tug outbound: empty(staging→nose) → dwell → push(nose follows spot→pushback) → detach dwell → empty(→staging).
  // The tug is planted `noseTugFt` ahead of the nose along the parked heading and
  // moves with the aircraft, so it stays in front of the nose throughout the push.
  const spotTugOut = offset(spot, parkedFwd, noseTugFt)
  const pushTug = offset(pushback, parkedFwd, noseTugFt)
  const tugOutEmptyStart = attachOutStart - tugDriveMs
  legs.push({ actorId: tugId, kind: 'tug_empty', startT: tugOutEmptyStart, endT: attachOutStart, path: [staging, spotTugOut], ease: 'inOut', headingMode: 'tangent', settleHeadingDeg: parkedHeading })
  dwells.push({ actorId: tugId, startT: attachOutStart, endT: attachOutEnd, position: spotTugOut, headingDeg: parkedHeading })
  legs.push({ actorId: tugId, kind: 'tow', startT: towOutStart, endT: towOutEnd, path: [spotTugOut, pushTug], ease: 'inOut', headingMode: 'fixed', fixedHeadingDeg: parkedHeading })
  dwells.push({ actorId: tugId, startT: detachOutStart, endT: detachOutEnd, position: pushTug, headingDeg: parkedHeading })
  legs.push({ actorId: tugId, kind: 'tug_empty', startT: detachOutEnd, endT: detachOutEnd + tugDriveMs, path: [pushTug, staging], ease: 'inOut', headingMode: 'tangent' })

  const startT = Math.min(airborne && airInMs > 0 ? descentStart : taxiInStart, tugInEmptyStart)
  const endT = Math.max(airborne && airOutMs > 0 ? climbEnd : taxiOutEnd, detachOutEnd + tugDriveMs)

  return {
    eventAllocationId: p.eventAllocationId,
    startT,
    endT,
    actors: [
      { kind: 'aircraft', id: acId },
      { kind: 'tug', id: tugId },
    ],
    legs,
    dwells: [...dwells, { actorId: acId, startT: p.arrival, endT: p.departure, position: spot, headingDeg: parkedHeading }],
  }
}

// ── Fuel truck (demand-driven, ephemeral) ───────────────────────────────────

/** Truck parks this far to the side of the aircraft so it clears the fuselage. */
const FUEL_SIDE_OFFSET_FT = 35

/**
 * Append a fuel-truck actor to a built plan when the aircraft requested fuel.
 * The truck dispatches from the fuel farm after the aircraft parks, drives to a
 * spot beside it, dwells while "fueling" (duration scaled to gallons), then
 * returns — all kept inside [arrival, departure] so it never outlives the stay.
 * Like the tug, every pose is a pure function of time (scrub-safe), so the truck
 * just appears/disappears as the clock crosses its window.
 */
function appendFuelTruckLegs(
  base: MovementPlan,
  p: EventPlacement,
  fuel: FuelRequest,
  cfg: MotionConfig,
  graph: FieldGraph,
  plan?: TaxiPlan | null
): MovementPlan {
  const truckId = fuelTruckActorId(p.eventAllocationId)
  const spot: Pt = [p.position[0], p.position[1]]
  // `p.rotation` is a scene Y-rotation; the motion math below works in the compass
  // convention (see easing.ts), so convert at the input boundary. computeActorPose
  // converts back to scene rotation on output.
  const parkedHeading = sceneRotationToCompass(p.rotation)
  const depot = graph.fuelDepot

  // Pull off to one side of the aircraft (perpendicular to its parked heading).
  const sideDir = fwdOf(parkedHeading + 90)
  const truckSpot = offset(spot, sideDir, FUEL_SIDE_OFFSET_FT)

  const window = p.departure - p.arrival
  if (window <= 0) return base
  const buffer = Math.min(cfg.fuelTruckBufferMs, window * 0.15)
  const driveMs = Math.min(SLOT_MS * 0.5, window * 0.15)
  // Scale the fueling dwell to gallons, but never let the round trip overrun the
  // stay (leave a 10% tail before departure).
  const fitDwell = window - buffer - 2 * driveMs - window * 0.1
  const dwellMs = Math.min(cfg.fuelBaseDwellMs + fuel.gallons * cfg.fuelMsPerGallon, fitDwell, window * 0.6)

  const dispatchStart = p.arrival + buffer
  const fuelStart = dispatchStart + driveMs
  const fuelEnd = fuelStart + dwellMs
  const returnEnd = fuelEnd + driveMs

  // Stay too short to fit a meaningful trip — skip the truck for this aircraft.
  if (dwellMs <= 0 || returnEnd >= p.departure) return base

  const legs: MotionLeg[] = [
    { actorId: truckId, kind: 'tug_empty', startT: dispatchStart, endT: fuelStart, path: smoothPath(groundPath(plan, graph, depot, truckSpot, dispatchStart)), ease: 'inOut', headingMode: 'tangent', settleHeadingDeg: parkedHeading },
    { actorId: truckId, kind: 'tug_empty', startT: fuelEnd, endT: returnEnd, path: smoothPath(groundPath(plan, graph, truckSpot, depot, fuelEnd)), ease: 'inOut', headingMode: 'tangent', launchHeadingDeg: parkedHeading },
  ]
  const dwells: MotionDwell[] = [
    { actorId: truckId, startT: fuelStart, endT: fuelEnd, position: truckSpot, headingDeg: parkedHeading },
  ]

  return {
    ...base,
    startT: Math.min(base.startT, dispatchStart),
    endT: Math.max(base.endT, returnEnd),
    actors: [...base.actors, { kind: 'fuel_truck', id: truckId }],
    legs: [...base.legs, ...legs],
    dwells: [...base.dwells, ...dwells],
  }
}

export function buildMovementPlan(
  p: EventPlacement,
  cfg: MotionConfig,
  graph?: FieldGraph | null,
  procedure: Procedure = 'self_park',
  plan?: TaxiPlan | null,
  catalog?: AircraftCatalog,
  fuel?: FuelRequest | null
): MovementPlan {
  if (!graph || graph.gates.length === 0) return straightSelfPark(p, cfg)
  const entry = catalog?.get(p.typeCode)
  const perf = runwayPerf(entry?.category, entry?.footprint.length)
  const base =
    cfg.enableTow && procedure !== 'self_park'
      ? buildTowPlan(p, cfg, graph, procedure, perf, tugNoseOffset(entry), plan)
      : buildSelfParkPlan(p, cfg, graph, perf, plan)
  if (cfg.enableFuelTrucks && fuel && fuel.gallons > 0) {
    return appendFuelTruckLegs(base, p, fuel, cfg, graph, plan)
  }
  return base
}

/** Build plans for every placement, keyed by eventAllocationId. */
export function buildAllPlans(
  placements: Record<string, EventPlacement>,
  cfg: MotionConfig,
  graph?: FieldGraph | null,
  procedureById?: Record<string, Procedure>,
  plan?: TaxiPlan | null,
  catalog?: AircraftCatalog,
  fuelByScheduleId?: Record<string, FuelRequest>
): Record<string, MovementPlan> {
  const out: Record<string, MovementPlan> = {}
  for (const [id, p] of Object.entries(placements)) {
    out[id] = buildMovementPlan(
      p,
      cfg,
      graph,
      procedureById?.[id] ?? 'self_park',
      plan,
      catalog,
      fuelByScheduleId?.[p.scheduleId]
    )
  }
  return out
}
